import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { activationsApiRequest } from '@/lib/activationsApiHelpers';
import { useToast } from '@/hooks/use-toast';
import type {
  ActivationResponse,
  AttachActivationRequest,
  CustomizeActivationRequest,
  ActivationShortCodeResponse,
  ActivationSessionResponse,
} from '@/types/generated/activations.generated';

const activationsKey = (eventId: string) =>
  ['activation-events', eventId, 'activations'] as const;
const shortCodeKey = (eventId: string, activationId: string) =>
  ['activation-events', eventId, 'activations', activationId, 'short-code'] as const;

/** Lists the activations attached to an event. */
export function useActivations(eventId: string | undefined) {
  return useQuery<ActivationResponse[]>({
    queryKey: activationsKey(eventId ?? ''),
    queryFn: () => activationsApiRequest('GET', `/admin/activation-events/${eventId}/activations`),
    enabled: !!eventId,
  });
}

/** Attaches a template to an event as a customized activation. */
export function useAttachActivation(eventId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: AttachActivationRequest) =>
      activationsApiRequest<ActivationResponse>(
        'POST',
        `/admin/activation-events/${eventId}/activations`,
        data,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: activationsKey(eventId) });
      toast({ title: 'Activation attached', description: 'The activation has been added to the event.' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to attach activation. Please try again.',
        variant: 'destructive',
      });
    },
  });
}

/** Updates an attached activation's customization (config / branding / order / status). */
export function useCustomizeActivation(eventId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ activationId, data }: { activationId: string; data: CustomizeActivationRequest }) =>
      activationsApiRequest<ActivationResponse>(
        'PUT',
        `/admin/activation-events/${eventId}/activations/${activationId}`,
        data,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: activationsKey(eventId) });
      toast({ title: 'Activation updated', description: 'The customization has been saved.' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update activation. Please try again.',
        variant: 'destructive',
      });
    },
  });
}

/** Fetches the QR/short-code payload (short code + runner URL) for an activation. */
export function useActivationShortCode(
  eventId: string | undefined,
  activationId: string | undefined,
) {
  return useQuery<ActivationShortCodeResponse>({
    queryKey: shortCodeKey(eventId ?? '', activationId ?? ''),
    queryFn: () =>
      activationsApiRequest(
        'GET',
        `/admin/activation-events/${eventId}/activations/${activationId}/short-code`,
      ),
    enabled: !!eventId && !!activationId,
  });
}

/** Starts a live participation session for an input activation. */
export function useStartActivationSession(eventId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (activationId: string) =>
      activationsApiRequest<ActivationSessionResponse>(
        'POST',
        `/admin/activation-events/${eventId}/activations/${activationId}/sessions/start`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: activationsKey(eventId) });
      toast({ title: 'Session started', description: 'The activation is now live and accepting participants.' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to start session. Please try again.',
        variant: 'destructive',
      });
    },
  });
}

/** Closes the live session for an activation. */
export function useCloseActivationSession(eventId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (activationId: string) =>
      activationsApiRequest(
        'POST',
        `/admin/activation-events/${eventId}/activations/${activationId}/sessions/close`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: activationsKey(eventId) });
      toast({ title: 'Session closed', description: 'The activation session has been closed.' });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to close session. Please try again.',
        variant: 'destructive',
      });
    },
  });
}
